import { ChargeEvent, FuelUpEvent, Geotab, KnownId, StatusData, Trip } from "@fairfleet/geotab";
import { ChargeEventRow, toChargeEventRows } from "../models/chargeEventRow";
import { chooseFuelCounter } from "../models/chooseFuelCounter";
import { ConsumptionRow, buildConsumptionRow } from "../models/consumptionRow";
import { CounterSample, counterUsage } from "../models/counterUsage";
import { EnergyDevice } from "../models/energyDevice";
import { chargeTotals, fillTotals } from "../models/eventTotals";
import { FuelUpEventRow, toFuelUpEventRows } from "../models/fuelUpEventRow";
import { createTripLookup } from "../models/tripContext";
import {
  RESULTS_LIMIT_CHARGE_EVENT,
  RESULTS_LIMIT_FUEL_UP_EVENT,
  RESULTS_LIMIT_STATUS_DATA,
  RESULTS_LIMIT_TRIP,
} from "@/constants/resultsLimits";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/** The three counter series read per vehicle. */
const SERIES = {
  vehicleFuel: KnownId.DiagnosticTotalFuelUsedId,
  telematicsFuel: KnownId.DiagnosticDeviceTotalFuelId,
  electric: KnownId.DiagnosticElectricEnergyOutId,
} as const;

type SeriesKey = keyof typeof SERIES;

/** Everything the report shows about one vehicle for one period. */
export interface DeviceEnergy {
  consumption: ConsumptionRow;
  chargeEvents: ChargeEventRow[];
  fuelUps: FuelUpEventRow[];
}

/**
 * Reads one vehicle worth of energy data and turns it into the report rows.
 *
 * Everything is asked for per vehicle rather than fleet-wide, on purpose. It costs more calls,
 * but the client batches `Get` into `ExecuteMultiCall` and charges them against its own rolling
 * budget, so the cost is time rather than failure — and in return a failure or a truncated list
 * is attributable to one vehicle instead of quietly shortening the whole report.
 *
 * @param geotabClient - The client to read through.
 * @param device - The vehicle to read.
 * @param fromDate - Start of the period.
 * @param toDate - End of the period.
 * @param signal - Abort signal.
 * @returns The vehicle rows for all three views.
 */
export async function fetchDeviceEnergy(
  geotabClient: Geotab,
  device: EnergyDevice,
  fromDate: Date,
  toDate: Date,
  signal?: AbortSignal,
): Promise<DeviceEnergy> {
  const search = { deviceSearch: { id: device.id }, fromDate, toDate };
  const samples = new Map<SeriesKey, CounterSample[]>();

  const [trips, chargeEvents, fuelUps] = await Promise.all([
    geotabClient.get("Trip", search, RESULTS_LIMIT_TRIP, signal),
    geotabClient.get("ChargeEvent", search, RESULTS_LIMIT_CHARGE_EVENT, signal),
    geotabClient.get("FuelUpEvent", search, RESULTS_LIMIT_FUEL_UP_EVENT, signal),
    fetchStatusDataComplete(geotabClient, {
      tasks: Object.entries(SERIES).map(([key, diagnosticId]) => ({
        key,
        deviceId: device.id,
        diagnosticId,
      })),
      fromDate,
      toDate,
      resultsLimit: RESULTS_LIMIT_STATUS_DATA,
      signal,
      onPage: (key, page) => {
        const existing = samples.get(key as SeriesKey);
        const converted = toSamples(page);
        if (existing) existing.push(...converted);
        else samples.set(key as SeriesKey, converted);
      },
    }),
  ]);

  return buildDeviceEnergy({ device, trips, chargeEvents, fuelUps, samples, failed: false });
}

/**
 * Builds the rows for a vehicle whose request failed, so it keeps its place in the report
 * instead of dropping out of it.
 *
 * @param device - The vehicle.
 * @returns The rows, marked as failed.
 */
export function failedDeviceEnergy(device: EnergyDevice): DeviceEnergy {
  return buildDeviceEnergy({
    device,
    trips: [],
    chargeEvents: [],
    fuelUps: [],
    samples: new Map(),
    failed: true,
  });
}

interface BuildInput {
  device: EnergyDevice;
  trips: Trip[];
  chargeEvents: ChargeEvent[];
  fuelUps: FuelUpEvent[];
  samples: Map<SeriesKey, CounterSample[]>;
  failed: boolean;
}

function buildDeviceEnergy(input: BuildInput): DeviceEnergy {
  const { device, trips, samples } = input;

  const tripAt = createTripLookup(trips);
  const chargeRows = toChargeEventRows(device, input.chargeEvents, tripAt);
  const fuelUpRows = toFuelUpEventRows(device, input.fuelUps, tripAt);

  const charge = chargeTotals(chargeRows);
  const fills = fillTotals(fuelUpRows);
  const fuel = chooseFuelCounter(
    counterUsage(samples.get("vehicleFuel") ?? []),
    counterUsage(samples.get("telematicsFuel") ?? []),
  );

  return {
    consumption: buildConsumptionRow({
      deviceId: device.id,
      name: device.name,
      licensePlate: device.licensePlate,
      trips: trips.map(trip => ({
        start: new Date(trip.start),
        stop: new Date(trip.stop),
        distance: trip.distance,
      })),
      fuel: fuel.usage,
      fuelSource: fuel.source,
      electric: counterUsage(samples.get("electric") ?? []),
      chargedKWh: charge.chargedKWh,
      chargeDurationMs: charge.durationMs,
      filledLiters: fills.filledLiters,
      fillsWithoutVolume: fills.fillsWithoutVolume,
      truncated: isTruncated(input),
      failed: input.failed,
    }),
    chargeEvents: chargeRows,
    fuelUps: fuelUpRows,
  };
}

/**
 * Whether any of the lists came back exactly at the results limit. Geotab cuts a result set off
 * at the limit and reports nothing about having done so, so the count is the only signal there is.
 */
function isTruncated({ trips, chargeEvents, fuelUps }: BuildInput) {
  return (
    trips.length >= RESULTS_LIMIT_TRIP ||
    chargeEvents.length >= RESULTS_LIMIT_CHARGE_EVENT ||
    fuelUps.length >= RESULTS_LIMIT_FUEL_UP_EVENT
  );
}

function toSamples(page: StatusData[]): CounterSample[] {
  return page.map(record => ({
    dateTime: new Date(record.dateTime),
    value: Number(record.data),
  }));
}
